import { reportsApi, type ReliabilityProofPackPreviewResponse } from '../services/reportsApi';
import { buildSafetyReportArtifactPath } from './reportFlow';

function appendQueryToPath(path: string, params: Record<string, string>): string {
  const [pathname, search = ''] = path.split('?');
  const next = new URLSearchParams(search);
  Object.entries(params).forEach(([key, value]) => {
    const normalized = String(value || '').trim();
    if (normalized) next.set(key, normalized);
  });
  const query = next.toString();
  return query ? `${pathname}?${query}` : pathname;
}

function resolveRepresentativeIncidentId(opened: any): string {
  return String(
    opened?.opened_representative_incident
      ? opened?.report?.workflow_anchor?.representative_incident_id
      : opened?.replacement_representative_incident_id
      || opened?.report?.workflow_anchor?.representative_incident_id
      || '',
  ).trim();
}

function firstNonEmpty(...values: Array<string | null | undefined>): string {
  for (const value of values) {
    const normalized = String(value || '').trim();
    if (normalized) return normalized;
  }
  return '';
}

function immutableSafetyReportStatus(status: unknown): boolean {
  const normalized = String(status || '').trim().toLowerCase();
  return normalized === 'finalized' || normalized === 'archived' || normalized === 'superseded';
}

export function resolvePersistedSafetyReportIdForIncidentContext(args: {
  proofPreview?: ReliabilityProofPackPreviewResponse | null;
  anchorIncidentId?: string | null;
  groupKey?: string | null;
}): string {
  const proofPreview = args.proofPreview;
  const persistedReportId = String(proofPreview?.persisted_report?.report_id || '').trim();
  if (!persistedReportId) return '';

  const expectedIncidentId = String(args.anchorIncidentId || '').trim();
  const expectedGroupKey = String(args.groupKey || '').trim();
  const anchorIncidentId = String(
    proofPreview?.evidence_pack?.workflow_anchor?.review_anchor_incident_id
      || proofPreview?.persisted_report?.workflow_anchor?.representative_incident_id
      || '',
  ).trim();
  const anchorGroupKey = String(proofPreview?.evidence_pack?.workflow_anchor?.group_key || '').trim();

  if (expectedIncidentId && anchorIncidentId && expectedIncidentId === anchorIncidentId) {
    return persistedReportId;
  }
  if (expectedGroupKey && anchorGroupKey && expectedGroupKey === anchorGroupKey) {
    return persistedReportId;
  }
  return '';
}

export async function resolveSafetyReportOpenPath(args: {
  fallbackPath: string;
  persistedReportId?: string | null;
}): Promise<string> {
  const persistedReportId = String(args.persistedReportId || '').trim();
  const fallbackPath = String(args.fallbackPath || '').trim();
  if (!persistedReportId || !fallbackPath) return fallbackPath;
  const fallbackWithPersistedReport = appendQueryToPath(fallbackPath, {
    report_id: persistedReportId,
  });

  try {
    const opened = await reportsApi.openSafetyReport(persistedReportId);
    const openedReport = opened?.report || {};
    const workflowAnchor = openedReport?.workflow_anchor || {};
    const anchorScope = workflowAnchor?.scope || {};
    const anchorTimeWindow = workflowAnchor?.time_window || {};
    const fallbackParams = new URLSearchParams(fallbackPath.split('?')[1] || '');
    if (immutableSafetyReportStatus(openedReport?.status)) {
      const representativeIncidentId = firstNonEmpty(
        workflowAnchor?.representative_incident_id,
        resolveRepresentativeIncidentId(opened),
        fallbackParams.get('incident_id'),
        fallbackParams.get('case_id'),
      );
      const anchorZones = Array.isArray(anchorScope?.zones) ? anchorScope.zones : [];
      return buildSafetyReportArtifactPath({
        reportId: persistedReportId,
        orgId: firstNonEmpty(openedReport?.org_id, fallbackParams.get('org_id')),
        siteId: firstNonEmpty(openedReport?.site_id, anchorScope?.site_id, fallbackParams.get('site_id')),
        workflowId: firstNonEmpty(opened?.workflow_id, openedReport?.workflow_id, workflowAnchor?.workflow_id, fallbackParams.get('workflow_id')),
        deviceId: firstNonEmpty(anchorScope?.device_id, fallbackParams.get('device_id')),
        zoneId: firstNonEmpty(anchorZones[0], fallbackParams.get('zone_id')),
        cameraId: firstNonEmpty(anchorScope?.camera_id, fallbackParams.get('camera_id')),
        reportFrom: firstNonEmpty(anchorTimeWindow?.start, anchorTimeWindow?.from, fallbackParams.get('report_from')),
        reportTo: firstNonEmpty(anchorTimeWindow?.end, anchorTimeWindow?.to, fallbackParams.get('report_to')),
        caseId: representativeIncidentId,
        incidentId: representativeIncidentId,
        policyType: firstNonEmpty(anchorScope?.risk_type, fallbackParams.get('policy_type')),
        policyName: firstNonEmpty(fallbackParams.get('policy_name')),
      });
    }
    const representativeIncidentId = resolveRepresentativeIncidentId(opened);
    const reportPayload = openedReport?.report_payload;
    const openedReportRecord = openedReport as Record<string, unknown>;
    const payloadHandoff = reportPayload && typeof reportPayload === 'object' && reportPayload.handoff && typeof reportPayload.handoff === 'object'
      ? reportPayload.handoff as Record<string, unknown>
      : null;
    const safetyCaseId = firstNonEmpty(
      String(payloadHandoff?.safetyCaseId || ''),
      String(payloadHandoff?.safety_case_id || ''),
      String(openedReportRecord.safety_case_id || ''),
      String(openedReportRecord.finding_id || ''),
      fallbackParams.get('safety_case_id'),
      fallbackParams.get('finding_id'),
    );
    return appendQueryToPath(fallbackWithPersistedReport, {
      report_id: persistedReportId,
      workflow_id: firstNonEmpty(
        opened?.workflow_id,
        openedReport?.workflow_id,
        workflowAnchor?.workflow_id,
        fallbackParams.get('workflow_id'),
      ),
      safety_case_id: safetyCaseId,
      case_id: representativeIncidentId,
      incident_id: representativeIncidentId,
      report_notice: String(opened?.notice || '').trim(),
    });
  } catch {
    return fallbackWithPersistedReport;
  }
}
